// DemiGaugeTopFixed.jsx
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './Speedometer.css';

export default function Speedometer({
  width = 350,
  height = 220,
  strokeWidth = 35,
  radius = 120,
  colors = ['#1663D6', '#EE952C', '#e40000'], // un par segment
  // segments = [1 / 2, 1 / 4, 1 / 4], // parts relatives (sommation ≈ 1)
  segments = [125 / 300, (222 - 125) / 300, (300 - 222) / 300], // parts relatives (sommation ≈ 1)
  labels = ['0', '125', '222', '300'], // 4 labels aux bornes
  labelOffset = 12,
  labelFontSize = 18,
  needlePercentage = 45, // en degrés, -90 = gauche, 0 = haut, 90 = droite
}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Supprime / réapplique la classe pour redémarrer l’animation à chaque apparition
          el.classList.remove('animate-once');
          void el.offsetWidth; // Force reflow → redémarrage propre
          el.classList.add('animate-once');
        } else {
          // quand l’élément sort de l’écran, on revient à zéro
          el.classList.remove('animate-once');
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const cx = width / 2;
  const cy = radius + 20;
  const r = radius;

  // normalisation prudente si la somme n'est pas exactement 1
  const sum = segments.reduce((a, b) => a + b, 0) || 1;
  const parts = segments.map((s) => (s <= 0 ? 0 : s / sum));

  // longueur totale du demi-cercle
  const semicircLen = Math.PI * r;

  // path du demi-cercle SUPÉRIEUR (gauche -> droite)
  const xLeft = cx - r;
  const xRight = cx + r;
  const yEnds = cy;
  const pathD = `M ${xLeft} ${yEnds} A ${r} ${r} 0 0 1 ${xRight} ${yEnds}`;

  // cumuls en longueur
  const cumLens = parts.reduce((acc, p, i) => {
    const prev = i === 0 ? 0 : acc[i - 1];
    acc.push(prev + p * semicircLen);
    return acc;
  }, []);

  // la ligne ci-dessus cumulait à tort, on corrige juste après:
  // Correction: on veut [0, p1*180, (p1+p2)*180, 180]
  const boundaries = [0];
  // parts.forEach((p, i) =>
  parts.forEach((p) =>
    boundaries.push(boundaries[boundaries.length - 1] + p * 180),
  );

  const labelRadius = r + strokeWidth / 2 + labelOffset;
  const deg2rad = (deg) => (deg * Math.PI) / 180;

  const pt = (deg, rad) => {
    const radian = deg2rad(deg);
    return {
      x: cx - rad * Math.cos(radian), // note: signe "moins" conservé
      y: cy - rad * Math.sin(radian),
    };
  };

  const needleRotation = -90 + (needlePercentage * 180) / 100; // -90 = gauche, 0 = haut, 90 = droite

  return (
    <div ref={ref} className="meter info-card">
      <svg
        width={width}
        height={height + strokeWidth + 4 * labelOffset}
        viewBox={`0 0 ${width} ${height + strokeWidth}`}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Demi-cercle supérieur à segments variables"
      >
        <defs>
          {/* Ombre douce */}
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="3"
              floodColor="#000"
              floodOpacity="0.25"
            />
          </filter>

          {/* Gradients pour chaque segment */}
          {colors.map((c, i) => (
            <linearGradient
              key={`grad-${i}`}
              id={`grad-${i}`}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor={c} stopOpacity="0.8" />
              <stop offset="100%" stopColor={c} stopOpacity="1" />
            </linearGradient>
          ))}
        </defs>

        {/* fond discret */}
        <path
          d={pathD}
          fill="none"
          stroke="#eeeeee"
          strokeWidth={strokeWidth}
          strokeLinecap="butt"
          opacity={0.25}
        />

        {/* Segments avec gradient, coins arrondis et ombre */}
        {parts.map((p, i) => {
          const segLen = p * semicircLen;
          const prevLen = i === 0 ? 0 : cumLens[i - 1];
          const dasharray = `${segLen.toFixed(2)} ${(semicircLen - segLen).toFixed(2)}`;
          const dashoffset = (-prevLen).toFixed(2);

          return (
            <path
              key={i}
              d={pathD}
              fill="none"
              stroke={`url(#grad-${i})`}
              strokeWidth={strokeWidth}
              strokeLinecap="butt"
              strokeDasharray={dasharray}
              strokeDashoffset={dashoffset}
              vectorEffect="non-scaling-stroke"
              filter="url(#shadow)"
            />
          );
        })}

        {/* Labels */}
        {boundaries.map((angle, i) => {
          const p = pt(angle, labelRadius);
          const yAdjust = -Math.max(8, labelFontSize / 3);
          return (
            <text
              key={`label-${i}`}
              x={p.x.toFixed(2)}
              y={(p.y + yAdjust).toFixed(2)}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={labelFontSize}
              fontFamily="Inter, Roboto, Arial, sans-serif"
              style={{
                userSelect: 'none',
                pointerEvents: 'none',
                fill: 'var(--table-text)',
              }}
            >
              {labels[i] ?? ''}
            </text>
          );
        })}

        <circle cx={cx} cy={cy} r={4} fill="#000" opacity={0.12} />
      </svg>

      {/* ton aiguille, inchangée */}
      <svg
        width="30"
        height="144"
        id="needle"
        style={{ '--needle-rotation': needleRotation + 'deg' }}
        viewBox="0 0 30 144"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="15.2089"
          cy="128.608"
          r="10.9086"
          fill="white"
          stroke="#01C738"
          strokeWidth="7"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17.3486 0.920656L25.3277 127.679L25.3166 127.679C25.322 127.833 25.3239 127.988 25.322 128.144C25.2587 133.535 20.8377 137.853 15.4474 137.79C10.0571 137.726 5.73875 133.305 5.80203 127.915C5.80386 127.759 5.80934 127.604 5.81838 127.45L5.80766 127.45L17.3486 0.920656ZM19.2238 134.487C22.4036 132.316 23.2212 127.978 21.0498 124.798C18.8784 121.618 14.5404 120.801 11.3606 122.972C8.18076 125.144 7.36323 129.482 9.53459 132.661C11.706 135.841 16.044 136.659 19.2238 134.487Z"
          fill="url(#paint0_linear_498_1180)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_498_1180"
            x1="8.57715"
            y1="129.079"
            x2="18.8019"
            y2="129.199"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.6874" stopColor="#3F4142" />
            <stop offset="0.6875" stopColor="#2C2C2C" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

Speedometer.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  strokeWidth: PropTypes.number,
  radius: PropTypes.number,
  colors: PropTypes.arrayOf(PropTypes.string),
  segments: PropTypes.arrayOf(PropTypes.number),
  labels: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  labelOffset: PropTypes.number,
  labelFontSize: PropTypes.number,
  needlePercentage: PropTypes.number,
};
